/**
 * BaseChart — обёртка над echarts-for-react с edge cases.
 *
 *  - Loading (Spinner поверх surface)
 *  - Empty (через <EmptyState />)
 *  - Error (danger-tinted блок в духе ErrorState)
 *  - применяет общий theme КОММА к option (axes, grid, tooltip, animation)
 *
 * Все графики в Кардиограмме используют этот компонент, чтобы:
 *  1) единый визуальный язык,
 *  2) не дублировать стили,
 *  3) удобный contract на состояния данных.
 */

import { useMemo, useRef, useEffect, type ReactNode } from 'react';
import ReactECharts from 'echarts-for-react';
import { Icon, Spinner } from '@/shared/ui';
import { EmptyState } from '@/components/ui/EmptyState';
import { chartTokens, chartFont } from '@/lib/echarts';
import type { EChartsOption } from 'echarts';

export interface BaseChartProps {
  /** Готовая ECharts-option. Сольётся с theme defaults. */
  option: EChartsOption;
  height?: number | string;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  /** true → показывает EmptyState вместо графика. */
  isEmpty?: boolean;
  emptyText?: string;
  emptyDescription?: string;
  /** Подпись под графиком (caption). */
  caption?: ReactNode;
  /** ARIA-метка для accessibility. */
  ariaLabel?: string;
  /** Дополнительные событийные хэндлеры. */
  onEvents?: Record<string, (params: unknown) => void>;
}

export function BaseChart({
  option,
  height = 360,
  isLoading,
  isError,
  errorMessage,
  isEmpty,
  emptyText = 'Нет данных',
  emptyDescription,
  caption,
  ariaLabel,
  onEvents,
}: BaseChartProps) {
  const chartRef = useRef<ReactECharts>(null);

  // ResizeObserver — графики корректно перерисовываются в grid-layout.
  useEffect(() => {
    const instance = chartRef.current?.getEchartsInstance();
    if (!instance) return;
    const ro = new ResizeObserver(() => {
      instance.resize();
    });
    const dom = instance.getDom() as HTMLElement | undefined;
    if (dom) ro.observe(dom);
    return () => ro.disconnect();
  }, []);

  const merged: EChartsOption = useMemo(() => {
    const t = chartTokens();
    const axisLine = { lineStyle: { color: t.divider } };
    const splitLine = { lineStyle: { color: t.divider, type: 'dashed' as const } };
    const axisLabel = { color: t.textMuted, fontFamily: chartFont, fontSize: 11 };
    const baseOption: EChartsOption = {
      animation: true,
      animationDuration: 300,
      animationEasing: 'cubicOut',
      textStyle: {
        fontFamily: chartFont,
        color: t.text,
      },
      grid: {
        left: 12,
        right: 24,
        top: 28,
        bottom: 28,
        containLabel: true,
      },
      tooltip: {
        backgroundColor: t.paper,
        borderColor: t.divider,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: {
          fontFamily: chartFont,
          color: t.text,
          fontSize: 12,
        },
        extraCssText: 'border-radius: 10px; box-shadow: 0 8px 20px rgba(17,24,39,0.08);',
      },
      legend: {
        textStyle: {
          color: t.textMuted,
          fontFamily: chartFont,
          fontSize: 12,
          fontWeight: 500,
        },
        icon: 'roundRect',
        itemHeight: 8,
        itemWidth: 14,
        top: 4,
        left: 0,
      },
      xAxis: {
        axisLine,
        splitLine: { show: false },
        axisLabel,
        axisTick: { show: false },
      },
      yAxis: {
        axisLine: { show: false },
        splitLine,
        axisLabel,
        axisTick: { show: false },
      },
      color: [t.primary, t.success, t.info, t.violet, t.textFaint],
    };
    // Глубокий merge через ECharts: возвращаем как есть, ECharts сам сольёт
    // top-level ключи. Для axisLine/splitLine/axisLabel — обрабатываем
    // как объект-defaults: пользовательский option должен явно переопределять.
    return { ...baseOption, ...option };
  }, [option]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div
          className="col center jcenter"
          style={{ height, width: '100%' }}
          aria-busy="true"
        >
          <Spinner />
        </div>
      );
    }
    if (isError) {
      return (
        <div
          role="alert"
          className="col center jcenter gap8"
          style={{
            height,
            width: '100%',
            border: '1px dashed color-mix(in srgb, var(--danger) 32%, transparent)',
            borderRadius: 'var(--r-lg)',
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
          }}
        >
          <Icon name="Alert" />
          <span className="muted" style={{ fontSize: 13.5 }}>
            {errorMessage ?? 'Не удалось загрузить данные'}
          </span>
        </div>
      );
    }
    if (isEmpty) {
      return (
        <div style={{ height, width: '100%' }}>
          <EmptyState
            variant="compact"
            text={emptyText}
            {...(emptyDescription ? { description: emptyDescription } : {})}
            icon={<Icon name="Activity" />}
          />
        </div>
      );
    }
    return (
      <div style={{ width: '100%', height }} role="img" aria-label={ariaLabel}>
        <ReactECharts
          ref={chartRef}
          option={merged}
          style={{ width: '100%', height: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
          lazyUpdate={true}
          {...(onEvents ? { onEvents } : {})}
        />
      </div>
    );
  };

  return (
    <div className="col gap8" style={{ width: '100%' }}>
      {renderBody()}
      {caption && (
        <div className="muted" style={{ fontSize: 12, padding: '0 4px', lineHeight: 1.45 }}>
          {caption}
        </div>
      )}
    </div>
  );
}
